import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Truck, Shield, Clock, MessageCircle, MapPin, Phone, Users, Package, Check, CreditCard, Store } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ProductCard from '../components/ProductCard';
import CategoryCard from '../components/CategoryCard';
import { Button } from '../components/ui/button';
import { WhatsAppContactCard } from '../components/WhatsAppChat';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HomePage = () => {
  const [categories, setCategories] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initData = async () => {
      try {
        await axios.post(`${API}/seed`).catch(() => {});
        const [catRes, prodRes] = await Promise.all([
          axios.get(`${API}/categories`),
          axios.get(`${API}/products?in_stock_only=true`)
        ]);
        setCategories(catRes.data);
        setFeaturedProducts(prodRes.data.slice(0, 8));
      } catch (error) {
        // silent
      } finally {
        setLoading(false);
      }
    };
    initData();
  }, []);

  return (
    <div className="min-h-screen flex flex-col" data-testid="home-page">
      <Navbar />
      
      <main className="flex-1">
        {/* ===== HERO ===== */}
        <section className="relative bg-green-900 overflow-hidden">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PHBhdGggZD0iTTM2IDE4YzMuMyAwIDYgMi43IDYgNnMtMi43IDYtNiA2LTYtMi43LTYtNiAyLjctNiA2LTZ6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-50" />
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20 relative">
            <div className="grid md:grid-cols-2 gap-10 items-center">
              <div className="text-white space-y-5">
                <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-1.5 text-sm text-green-100 border border-white/10">
                  <MapPin className="w-3.5 h-3.5" />
                  Eersterust, Mamelodi, Silverton, Nellmapius
                </div>
                <h1 className="text-4xl sm:text-5xl lg:text-[3.5rem] font-bold leading-[1.1] tracking-tight" data-testid="hero-title">
                  NarkOnline &mdash; Your Local Grocery Store, Online
                </h1>
                <p className="text-base text-green-100/80 max-w-md leading-relaxed">
                  Shop groceries and essentials in Eersterust and surrounding areas. We source your items 
                  from major retailers and deliver them straight to your door using local tuk-tuk drivers.
                </p>
                <p className="text-lg font-semibold text-white">Fast. Affordable. Local.</p>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-green-100 text-sm">
                    <Check className="w-4 h-4 text-green-300" />
                    <span>R30 flat delivery fee</span>
                  </div>
                  <div className="flex items-center gap-2 text-green-100 text-sm">
                    <Check className="w-4 h-4 text-green-300" />
                    <span>Same-day delivery (order before 2pm)</span>
                  </div>
                  <div className="flex items-center gap-2 text-green-100 text-sm">
                    <Check className="w-4 h-4 text-green-300" />
                    <span>Secure payments via Paystack</span>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                  <Link to="/products">
                    <Button className="bg-white text-green-900 hover:bg-green-50 rounded-full px-7 py-5 text-base font-semibold shadow-lg" data-testid="shop-now-btn">
                      Start Shopping
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                </div>
              </div>
              
              <div className="relative hidden md:block">
                <img 
                  src="https://images.unsplash.com/photo-1637087788835-4f051e32bfa1?w=550&h=380&fit=crop"
                  alt="Fresh grocery delivery box with fruits and vegetables - NarkOnline Eersterust"
                  className="rounded-2xl shadow-2xl border-4 border-white/10"
                />
                <div className="absolute -bottom-4 -left-4 bg-white rounded-xl shadow-lg p-3 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                    <Truck className="w-5 h-5 text-green-700" />
                  </div>
                  <div>
                    <p className="text-xs text-stone-500">Delivery fee</p>
                    <p className="font-bold text-green-900">R30 flat rate</p>
                  </div>
                </div>
                <div className="absolute -top-3 -right-3 bg-orange-500 text-white rounded-full px-4 py-2 text-sm font-bold shadow-lg">
                  Same-day delivery
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ===== TRUST + CLARITY STRIP ===== */}
        <section className="bg-stone-900 text-white py-6">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 space-y-4 text-sm text-stone-300 leading-relaxed">
            <p>
              <strong className="text-white">Nark Online (Pty) Ltd is the sole seller of all products on this platform.</strong>{' '}
              We source items from major retailers and deliver them directly to customers.
            </p>
            <p>
              <strong className="text-white">Payments are securely processed via Paystack.</strong>{' '}
              Orders are confirmed once payment is received.
            </p>
            <p>
              NarkOnline is the <strong className="text-white">merchant of record</strong> and handles all payments, refunds, and customer support.
            </p>
            <p>
              Prices may differ from in-store pricing due to sourcing and service costs.
            </p>
          </div>
        </section>

        {/* ===== HOW IT WORKS ===== */}
        <section className="py-14 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-center text-2xl font-bold text-stone-900 mb-10">How NarkOnline Works</h2>
            <div className="grid md:grid-cols-4 gap-6">
              {[
                { step: '1', title: 'Browse & Add', desc: 'Select groceries from our catalogue and add them to your cart.' },
                { step: '2', title: 'Checkout & Pay', desc: 'Pay securely using card or bank transfer via Paystack. A R30 delivery fee is added at checkout.' },
                { step: '3', title: 'We Source & Prepare', desc: 'We purchase your items from trusted retailers and prepare your order.' },
                { step: '4', title: 'Tuk-Tuk Delivery', desc: 'A driver from the Eersterust Tuk Tuk Association delivers your order to your door — usually the same day.' },
              ].map((item) => (
                <div key={item.step} className="text-center">
                  <div className="w-12 h-12 rounded-full bg-green-900 text-white text-lg font-bold flex items-center justify-center mx-auto mb-3">
                    {item.step}
                  </div>
                  <h3 className="font-semibold text-stone-900 mb-1">{item.title}</h3>
                  <p className="text-sm text-stone-500 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ===== DELIVERY INFORMATION ===== */}
        <section className="py-10 bg-green-50 border-y border-green-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-center text-xl font-bold text-stone-900 mb-6">Delivery Information</h2>
            <div className="grid md:grid-cols-4 gap-6 text-center">
              <div>
                <MapPin className="w-6 h-6 text-green-700 mx-auto mb-2" />
                <h3 className="font-semibold text-stone-900 text-sm mb-1">Serving</h3>
                <p className="text-sm text-stone-600">Eersterust, Mamelodi, Silverton & Nellmapius</p>
              </div>
              <div>
                <Truck className="w-6 h-6 text-green-700 mx-auto mb-2" />
                <h3 className="font-semibold text-stone-900 text-sm mb-1">Delivery Partner</h3>
                <p className="text-sm text-stone-600">Eersterust Tuk Tuk Association</p>
              </div>
              <div>
                <CreditCard className="w-6 h-6 text-green-700 mx-auto mb-2" />
                <h3 className="font-semibold text-stone-900 text-sm mb-1">Delivery Fee</h3>
                <p className="text-sm text-stone-600">R30 flat rate</p>
              </div>
              <div>
                <Clock className="w-6 h-6 text-green-700 mx-auto mb-2" />
                <h3 className="font-semibold text-stone-900 text-sm mb-1">Same-Day Delivery</h3>
                <p className="text-sm text-stone-600">For orders placed before 2pm</p>
              </div>
            </div>
          </div>
        </section>

        {/* ===== SHOP BY CATEGORY ===== */}
        <section className="py-14 bg-stone-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl font-bold text-stone-900">Shop by Category</h2>
              <Link to="/products" className="text-green-900 font-semibold hover:underline hidden sm:block text-sm">
                View All
              </Link>
            </div>
            {loading ? (
              <div className="flex justify-center py-12"><div className="spinner"></div></div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="categories-grid">
                {categories.slice(0, 8).map((category, index) => (
                  <CategoryCard key={category.id} category={category} index={index} />
                ))}
              </div>
            )}
          </div>
        </section>

        {/* ===== POPULAR PRODUCTS ===== */}
        <section className="py-14 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl font-bold text-stone-900">Popular Products</h2>
              <Link to="/products" className="text-green-900 font-semibold hover:underline hidden sm:block text-sm">
                View All Products
              </Link>
            </div>
            {loading ? (
              <div className="flex justify-center py-12"><div className="spinner"></div></div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5" data-testid="products-grid">
                {featuredProducts.map((product, index) => (
                  <ProductCard key={product.id} product={product} index={index} />
                ))}
              </div>
            )}
            <div className="text-center mt-10">
              <Link to="/products">
                <Button className="btn-primary rounded-full px-8" data-testid="view-all-products">
                  View All Products
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* ===== ABOUT NARKONLINE ===== */}
        <section className="py-14 bg-green-900 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-2xl font-bold mb-4">Built for Eersterust, by Eersterust.</h2>
                <p className="text-green-100/80 leading-relaxed mb-4">
                  NarkOnline was created to make grocery shopping easier, more affordable, and more convenient 
                  for our community. Instead of travelling to major stores or relying on inconsistent local 
                  pricing, customers can order online and receive reliable delivery.
                </p>
                <p className="text-green-100/80 leading-relaxed mb-6">
                  We source products from major retailers and deliver them using local tuk-tuk drivers, 
                  creating both convenience for customers and income opportunities for the community.
                </p>
                <p className="text-white font-semibold">Every order supports a local driver.</p>
                <div className="grid grid-cols-3 gap-4 mt-6">
                  <div className="text-center">
                    <Package className="w-6 h-6 text-green-300 mx-auto mb-1" />
                    <p className="text-2xl font-bold">88+</p>
                    <p className="text-xs text-green-200">Products</p>
                  </div>
                  <div className="text-center">
                    <Users className="w-6 h-6 text-green-300 mx-auto mb-1" />
                    <p className="text-2xl font-bold">20+</p>
                    <p className="text-xs text-green-200">Local Drivers</p>
                  </div>
                  <div className="text-center">
                    <MapPin className="w-6 h-6 text-green-300 mx-auto mb-1" />
                    <p className="text-2xl font-bold">4</p>
                    <p className="text-xs text-green-200">Areas Served</p>
                  </div>
                </div>
              </div>
              <div className="relative rounded-2xl overflow-hidden">
                <img 
                  src="/tuktuk.jpg"
                  alt="Eersterust Tuk Tuk Association delivery partner"
                  className="w-full rounded-2xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* ===== WHY CHOOSE US ===== */}
        <section className="py-14 bg-white">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl font-bold text-stone-900 mb-8">Why Choose NarkOnline</h2>
            <div className="grid sm:grid-cols-2 gap-4 text-left">
              {[
                'No need to travel to big stores',
                'Transparent, upfront pricing',
                'Secure payments via Paystack',
                'Reliable same-day delivery',
                'Community-driven platform',
              ].map((item) => (
                <div key={item} className="flex items-center gap-3 bg-stone-50 rounded-lg p-4">
                  <Check className="w-5 h-5 text-green-700 flex-shrink-0" />
                  <span className="text-stone-800 font-medium text-sm">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ===== DRIVE WITH US ===== */}
        <section className="py-14 bg-orange-50 border-y border-orange-100">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl font-bold text-stone-900 mb-3">Drive With Us</h2>
            <p className="text-stone-600 mb-6 max-w-xl mx-auto">
              Own a tuk-tuk or vehicle in Eersterust? Join our delivery team. 
              Flexible hours, earn per delivery, and serve your community.
            </p>
            <Link to="/driver/signup">
              <Button className="bg-orange-600 hover:bg-orange-700 text-white rounded-full px-8 py-5 text-base" data-testid="become-driver-btn">
                Apply to Become a Driver
              </Button>
            </Link>
          </div>
        </section>

        {/* ===== CONTACT & SUPPORT ===== */}
        <section className="py-14 bg-stone-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-12 items-start">
              <div>
                <h2 className="text-2xl font-bold text-stone-900 mb-3">Need Help With an Order?</h2>
                <div className="space-y-4 mt-6">
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-green-700" />
                    <div>
                      <p className="text-sm text-stone-500">WhatsApp</p>
                      <a href="tel:+27615304587" className="font-semibold text-stone-900">061 530 4587</a>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <MessageCircle className="w-5 h-5 text-green-700" />
                    <div>
                      <p className="text-sm text-stone-500">Email</p>
                      <a href="mailto:admin@narkonline.com" className="font-semibold text-stone-900">admin@narkonline.com</a>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-green-700" />
                    <div>
                      <p className="text-sm text-stone-500">Support Hours</p>
                      <p className="font-semibold text-stone-900">Mon–Sat: 7am – 6pm</p>
                      <p className="font-semibold text-stone-900">Sun: 8am – 2pm</p>
                    </div>
                  </div>
                </div>
              </div>
              <WhatsAppContactCard />
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default HomePage;
